/**
 * RBACContext - Unified Role-Based Access Control Context
 *
 * This context provides the SINGLE SOURCE OF TRUTH for all RBAC data.
 * It fetches from the backend /api/v1/admin/me endpoint, ensuring:
 * - Consistent role values across all components
 * - Single API call for all RBAC data
 * - Easy debugging (single logging point)
 * - Backend handles all complex role logic
 *
 * Key features:
 * - Single `rbacReady` flag that waits for ALL RBAC data to load
 * - All derived values (role, isSuperadmin, companies, permissions, etc.) from backend
 * - Refresh mechanism for re-fetching after role changes
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getAdminMe } from '@/lib/adminApi';
import { identifyUser, setCompanyGroup } from '@/lib/analytics';
import { useTimeZonePrefill } from '@/hooks/useTimeZonePrefill';
import type { IndustryType, CompanyThemeJson } from '@/types';
import {
  RBACContext,
  type UserRole,
  type AdminUser,
  type UserCompany,
  type UserMembership,
  type UserProfile,
  type RBACContextType,
  type UserCapabilities,
} from './RBACContext';

/**
 * RBACProvider - Fetches all RBAC data from backend /api/v1/admin/me
 *
 * This is the SINGLE SOURCE OF TRUTH for RBAC in the application.
 * It replaces the previous approach of multiple direct Supabase queries.
 */
export function RBACProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, loading: authLoading } = useAuth();
  // T-226: fill in the browser's time zone at sign-in, never over a chosen one.
  useTimeZonePrefill();

  // Single loading state for ALL RBAC data
  const [loading, setLoading] = useState(true);

  // Track the last user ID we loaded RBAC data for
  // This allows us to detect when a NEW user logs in and we need to re-fetch
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // User state from backend
  const [user, setUser] = useState<AdminUser | null>(null);

  // Role state
  const [role, setRole] = useState<UserRole | null>(null);

  // Companies state
  const [companies, setCompanies] = useState<UserCompany[]>([]);

  // Memberships state
  const [memberships, setMemberships] = useState<UserMembership[]>([]);

  // Permissions state
  const [permissions, setPermissions] = useState<string[]>([]);

  // T-068 P3: server-computed feature-visibility flags.
  const [capabilities, setCapabilities] = useState<UserCapabilities>({ report_subscriptions_admin: false, unbilled_deployments_view: false });

  // Superadmin state (from backend is_superadmin field)
  const [isSuperadminFlag, setIsSuperadminFlag] = useState(false);

  // Track PostHog identification to avoid re-identifying on token refresh
  const identifiedUserRef = useRef<string | null>(null);

  /**
   * Fetch all RBAC data from backend /api/v1/admin/me
   * This single API call replaces 4 separate Supabase queries
   */
  const fetchAllRBACData = useCallback(async () => {
    if (!authUser) {
      // Reset all state when not authenticated
      setUser(null);
      setRole(null);
      setCompanies([]);
      setMemberships([]);
      setPermissions([]);
      setCapabilities({ report_subscriptions_admin: false, unbilled_deployments_view: false });
      setIsSuperadminFlag(false);
      setLoadedForUserId(null);
      identifiedUserRef.current = null;
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Single API call to backend - the source of truth
      const data = await getAdminMe();

      console.log('[RBACContext] Fetched RBAC data from backend:', {
        userId: data.user.id,
        email: data.user.email,
        is_superadmin: data.is_superadmin,
        companies: data.companies.length,
        memberships: data.memberships.length,
        permissions: data.permissions.length,
      });

      // Set user data
      setUser(data.user);

      // Set superadmin flag from backend (authoritative source)
      setIsSuperadminFlag(data.is_superadmin);

      // Determine role from backend data
      let determinedRole: UserRole = 'user';
      if (data.is_superadmin) {
        determinedRole = 'superadmin';
      } else if (data.memberships.some((m) => m.role === 'admin')) {
        determinedRole = 'admin';
      } else if (data.memberships.some((m) => m.role === 'company_admin')) {
        determinedRole = 'company_admin';
      } else if (data.memberships.some((m) => m.role === 'group_admin')) {
        determinedRole = 'group_admin';
      }
      setRole(determinedRole);
      console.log('[RBACContext] Role determined:', determinedRole, '| isSuperadmin:', data.is_superadmin);

      // Set companies with proper typing
      setCompanies(
        data.companies.map((a) => ({
          id: a.id,
          name: a.name,
          slug: a.slug,
          is_internal: a.is_internal,
          industry: (a as { industry?: IndustryType }).industry,
          role: a.role,
          logo_url: (a as { logo_url?: string | null }).logo_url || null,
          theme_json: (a as { theme_json?: CompanyThemeJson | null }).theme_json || null,
        }))
      );

      // Set memberships
      setMemberships(data.memberships);

      // Set permissions
      setPermissions(data.permissions);
      setCapabilities({
        report_subscriptions_admin: data.capabilities?.report_subscriptions_admin === true,
        unbilled_deployments_view: data.capabilities?.unbilled_deployments_view === true,
      });

      // Identify user in PostHog (once per user session)
      if (identifiedUserRef.current !== data.user.id) {
        identifyUser(data.user.id, {
          email: data.user.email,
          first_name: data.user.first_name,
          last_name: data.user.last_name,
          role: determinedRole,
          is_internal: data.user.is_internal,
          is_superadmin: data.is_superadmin,
        });
        // Set company group for company-level analytics
        const primaryCompany = data.companies[0];
        if (primaryCompany) {
          setCompanyGroup(primaryCompany.id, {
            name: primaryCompany.name,
            industry: (primaryCompany as { industry?: IndustryType }).industry,
          });
        }
        identifiedUserRef.current = data.user.id;
      }

      // Mark that we've loaded data for this user
      setLoadedForUserId(authUser.id);
    } catch (error) {
      // Handle auth errors gracefully - user might not be fully authenticated yet
      // This can happen during login flow or if token is expired
      if (error instanceof Error && error.message.includes('not authenticated')) {
        console.log('[RBACContext] User not yet authenticated, waiting...');
      } else {
        console.error('[RBACContext] Failed to fetch RBAC data:', error);
      }

      // Set defaults on error
      console.warn('[RBACContext] ⚠️ RBAC fetch failed - defaulting to "user" role. This may hide filters.');
      setUser(null);
      setRole('user');
      setCompanies([]);
      setMemberships([]);
      setPermissions([]);
      setCapabilities({ report_subscriptions_admin: false, unbilled_deployments_view: false });
      setIsSuperadminFlag(false);
      // Still mark as loaded for this user (with defaults) to prevent infinite loading
      if (authUser) {
        setLoadedForUserId(authUser.id);
      }
    } finally {
      // Only set loading=false after ALL data is processed
      setLoading(false);
    }
  }, [authUser]);

  // Fetch RBAC data when auth user ID changes (not on every object reference change)
  // This prevents re-fetching when only the token refreshes but the user hasn't changed
  useEffect(() => {
    // Wait for auth to finish loading before fetching RBAC data
    if (authLoading) {
      return;
    }

    fetchAllRBACData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, authLoading]);

  // Derive computed values and build legacy profile for backward compatibility
  const value = useMemo<RBACContextType>(() => {
    const primaryCompany = companies.length > 0 ? companies[0] : null;

    // Build profile interface from user data
    const profile: UserProfile | null = user
      ? {
          user_id: user.id,
          first_name: user.first_name || null,
          last_name: user.last_name || null,
          is_internal: user.is_internal,
          default_company_id: user.default_company_id || null,
          default_group_id: user.default_group_id || null,
        }
      : null;

    // CRITICAL: rbacReady must be false if:
    // 1. Auth is still loading
    // 2. RBAC fetch is in progress
    // 3. A user is logged in but we haven't loaded RBAC data for them yet
    //    (This happens right after login when loadedForUserId is stale)
    const userNeedsRBACLoad = authUser !== null && loadedForUserId !== authUser.id;
    const isReady = !loading && !authLoading && !userNeedsRBACLoad;

    return {
      // Loading states
      rbacReady: isReady,
      loading: loading || authLoading || userNeedsRBACLoad,

      // Role data
      role,
      isGroupAdmin: role === 'group_admin',
      isCompanyAdmin: role === 'company_admin',
      isAdmin: role === 'admin' || role === 'superadmin',
      isSuperadmin: isSuperadminFlag,

      // Profile data (legacy interface)
      profile,
      isInternal: user?.is_internal ?? false,

      // Company data
      company: primaryCompany,
      companies,

      // NEW: Full user object
      user,

      // NEW: Memberships
      memberships,

      // NEW: Permissions
      permissions,

      // T-068 P3: feature-visibility flags
      capabilities,

      // Refresh function
      refreshRBAC: fetchAllRBACData,
    };
  }, [loading, authLoading, authUser, loadedForUserId, role, isSuperadminFlag, user, companies, memberships, permissions, capabilities, fetchAllRBACData]);

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}
